import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

interface HtmlCertificatePreviewProps {
    htmlContent: string | null;
    loading?: boolean;
    error?: string | null;
    height?: string | number;
    width?: string | number;
    scale?: number;
    border?: boolean;
    maxWidth?: number;
}

const HtmlCertificatePreview: React.FC<HtmlCertificatePreviewProps> = ({
    htmlContent,
    loading = false,
    error = null,
    height = '400px',
    width = '100%',
    scale = 1,
    border = true,
    maxWidth = 800
}) => {
    const [iframeContent, setIframeContent] = useState<string>('');

    useEffect(() => {
        if (htmlContent) {
            // Inject CSS to limit content width and improve scaling
            const styleTag = `<style>
                body { 
                    margin: 0; 
                    padding: 0; 
                    max-width: ${maxWidth}px;
                    width: ${maxWidth}px;
                    overflow-x: hidden;
                }
                * {
                    max-width: 100%;
                    box-sizing: border-box;
                }
            </style>`;

            let modifiedContent = htmlContent;

            // Insert style tag in head or after opening html tag
            if (modifiedContent.includes('<head>')) {
                modifiedContent = modifiedContent.replace('<head>', '<head>' + styleTag);
            } else if (modifiedContent.includes('<html>')) {
                modifiedContent = modifiedContent.replace('<html>', '<html>' + styleTag);
            } else {
                modifiedContent = styleTag + modifiedContent;
            }

            setIframeContent(modifiedContent);
        }
    }, [htmlContent, maxWidth]);

    const containerStyle: React.CSSProperties = {
        height: typeof height === 'number' ? `${height}px` : height,
        width: typeof width === 'number' ? `${width}px` : width,
        border: border ? '1px solid #ddd' : 'none',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'hidden',
        position: 'relative'
    };

    const iframeStyle: React.CSSProperties = {
        width: scale !== 1 ? `${100 / scale}%` : '100%',
        height: scale !== 1 ? `${100 / scale}%` : '100%',
        border: 'none',
        borderRadius: border ? '4px' : '0',
        transform: scale !== 1 ? `scale(${scale})` : 'none',
        transformOrigin: 'top left',
        position: scale !== 1 ? 'absolute' : 'static',
        top: 0,
        left: 0
    };

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}>
                    <Loader2 className="animate-spin" size={20} />
                    <span>Loading certificate...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...containerStyle, padding: '16px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#dc3545',
                    backgroundColor: '#f8d7da',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #f5c6cb',
                    width: '100%'
                }}>
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (!htmlContent) {
        return (
            <div style={containerStyle}>
                <div style={{ color: '#6c757d' }}>
                    No certificate content available
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <iframe
                srcDoc={iframeContent}
                style={iframeStyle}
                title="Certificate Preview"
                sandbox="allow-same-origin"
            />
        </div>
    );
};

export default HtmlCertificatePreview;